
import { FormState } from '../types';
import { calculateClassDuration, calculateClassTimes, isSubmittedOnTime } from './dateTimeUtils';
import { prepareDevelopmentReport } from './reportPreparation';
import { DateTime } from 'luxon';

// Helper function to safely convert a value to number or return null
const safeNumberConversion = (value: string | string[] | boolean | File[] | undefined): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (value === '') return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

// Prepare form data for submission
export const prepareFormDataForSubmission = (
  formData: FormState['formData'], 
  checkInPhotoUrls: string[], 
  worksheetUrls: string[],
  isAdmin: boolean = false
) => {
  // Calculate the class duration in hours
  const teachingDuration = formData.teaching_duration as string;
  const classDuration = calculateClassDuration(teachingDuration);
  
  // Calculate start and end dates
  const teachingDate = formData.teaching_date as string;
  const teachingTimeSlot = formData.teaching_time_slot as string;
  
  let startDate: string | null = null;
  let endDate: string | null = null;
  
  try {
    if (teachingDate && teachingTimeSlot) {
      const classTimes = calculateClassTimes(teachingDate, teachingTimeSlot, classDuration);
      startDate = classTimes.startDate;
      endDate = classTimes.endDate;
    }
  } catch (error) {
    console.error('Error calculating class times:', error);
  }

  formData.start_date = startDate;
  formData.end_date = endDate;
  
  // Calculate if submission is on time based on the end date
  const submitted_on_time = isSubmittedOnTime(endDate);
  
  // Prepare the development report content
  const developmentReport = prepareDevelopmentReport(formData);

  // Extract teacher display name from teacher_id_name_display if available
  const teacherDisplay = formData.teacher_id_name_display as string || formData.teacher_id as string;
  
  // Handle subject_data properly - make sure it's JSON or null
  let subjectData = null;
  if (formData.subject_data) {
    if (typeof formData.subject_data === 'string') {
      // It's already a string - assume it's valid JSON
      subjectData = formData.subject_data;
    } else {
      // Convert to JSON string
      try {
        subjectData = JSON.stringify(formData.subject_data);
      } catch (e) {
        console.error('Error converting subject_data to JSON:', e);
        subjectData = null;
      }
    }
  }
  
  // Debug logs for worksheet IDs
  console.log('Raw worksheet_id from form:', formData.worksheet_id);
  console.log('Raw next_worksheet_id from form:', formData.next_worksheet_id);
  
  // Safely convert worksheet_id and next_worksheet_id to numbers or null
  const worksheetId = safeNumberConversion(formData.worksheet_id);
  const nextWorksheetId = safeNumberConversion(formData.next_worksheet_id);
  
  console.log('Processed worksheet_id:', worksheetId);
  console.log('Processed next_worksheet_id:', nextWorksheetId);
  
  return {
    submitter_email: formData.submitter_email as string,
    teacher_id: formData.teacher_id as string,
    teacher_display: teacherDisplay,
    agreed_to_terms: formData.agreed_to_terms as boolean,
    
    teaching_location: formData.teaching_location as string,
    teaching_type: formData.teaching_type as string,
    teaching_date: DateTime.fromISO(formData.teaching_date as string).setZone('Asia/Bangkok').toFormat('yyyy-MM-dd'),
    teaching_time_slot: formData.teaching_time_slot as string,
    teaching_duration: formData.teaching_duration as string,
    
    // New fields
    status: 'WAITING',
    class_duration: classDuration,
    start_date: startDate,
    end_date: endDate,
    submitted_on_time: submitted_on_time,
    created_by_admin: isAdmin,
    
    student_id: formData.student_id as string,
    student_full_name: formData.student_full_name as string,
    student_nickname: formData.student_nickname as string,
    student_grade_level: formData.student_grade_level as string,
    
    subject: formData.subject as string,
    learning_status: formData.learning_status as string,
    
    // Worksheet data and other subject
    subject_data: subjectData,
    other_subject: formData.other_subject as boolean || false,
    worksheet_id: worksheetId,
    
    // Next topic other subject flag
    next_topic_other_subject: formData.next_topic_other_subject as boolean || false,
    next_worksheet_id: nextWorksheetId,
    
    check_in_photo_urls: checkInPhotoUrls,
    
    development_report: developmentReport,
    teaching_content: formData.topic_learned as string,
    homework_assignment: formData.homework_assigned as string,
    next_class_preparation: formData.next_topic as string,
    
    worksheet_urls: worksheetUrls,
    worksheet_drive_link: formData.worksheet_drive_link as string,
  };
};
